'use client';

import { useEffect, useRef } from 'react';
import type { Key } from '@react-types/shared';
import type { RootMenuTriggerState, SubmenuTriggerState } from 'react-stately';
import type { MenuSubmenuTriggerMode } from '../menuSubmenuContext';

/**
 * Keeps click-mode submenus open across sibling hover focus changes while still closing on sibling press.
 */
export const useMenuSubmenuClickModeSuppression = ({
    triggerMode,
    parentMenuRef,
    itemRef,
    submenuTriggerState,
}: {
    triggerMode: MenuSubmenuTriggerMode;
    parentMenuRef?: { current: HTMLElement | null } | null;
    itemRef: { current: HTMLElement | null };
    submenuTriggerState: Pick<SubmenuTriggerState, 'isOpen' | 'close'>;
}): void => {
    const isClickTriggerMode = triggerMode === 'click';
    const submenuTriggerStateRef = useRef(submenuTriggerState);
    submenuTriggerStateRef.current = submenuTriggerState;

    useEffect(() => {
        const parentMenuElement = parentMenuRef?.current;
        if (!parentMenuElement || !isClickTriggerMode) return;

        const isOpen = () => submenuTriggerStateRef.current.isOpen;

        const suppressFocusClose = (event: FocusEvent) => {
            if (!isOpen()) return;
            // Let React Aria restore keyboard focus to this trigger when closing the submenu.
            if (itemRef.current && event.target instanceof Node && itemRef.current.contains(event.target)) return;
            event.stopImmediatePropagation();
        };

        const closeOnSiblingPress = (event: PointerEvent) => {
            if (!isOpen()) return;

            const target = event.target as globalThis.Node | null;
            if (target && itemRef.current?.contains(target)) return;
            submenuTriggerStateRef.current.close();
        };

        parentMenuElement.addEventListener('focusin', suppressFocusClose, { capture: true });
        parentMenuElement.addEventListener('pointerdown', closeOnSiblingPress, { capture: true });

        return () => {
            parentMenuElement.removeEventListener('focusin', suppressFocusClose, { capture: true });
            parentMenuElement.removeEventListener('pointerdown', closeOnSiblingPress, { capture: true });
        };
    }, [isClickTriggerMode, itemRef, parentMenuRef]);
};

type PointerModeInput = {
    triggerMode: MenuSubmenuTriggerMode;
    triggerKey: Key;
    rootMenuTriggerState?: Pick<RootMenuTriggerState, 'expandedKeysStack' | 'closeSubmenu'> | null;
    submenuTriggerState: Pick<SubmenuTriggerState, 'isOpen' | 'submenuLevel' | 'close'>;
    getSubmenuTriggerMode: (key: Key | undefined) => MenuSubmenuTriggerMode | undefined;
    onPress?: (event: unknown) => void;
    onHoverChange?: (isHovered: boolean) => void;
};

type PointerPressEvent = {
    pointerType?: string;
};

const CLICK_MODE_TOGGLE_POINTER_TYPES = new Set(['mouse', 'touch', 'pen', 'virtual']);

type PointerModeOutput = {
    onPress: (event: PointerPressEvent) => void;
    onHoverStart: () => void;
    onHoverChange: (isHovered: boolean) => void;
};

/**
 * Applies Roma's pointer-only trigger-mode policy without changing keyboard submenu semantics.
 */
export const useMenuSubmenuPointerMode = ({
    triggerMode,
    triggerKey,
    rootMenuTriggerState,
    submenuTriggerState,
    getSubmenuTriggerMode,
    onPress,
    onHoverChange,
}: PointerModeInput): PointerModeOutput => {
    const isClickTriggerMode = triggerMode === 'click';

    const getOpenSiblingSubmenuKey = () => rootMenuTriggerState?.expandedKeysStack[submenuTriggerState.submenuLevel];

    const closeOpenHoverSiblingSubmenu = () => {
        if (!rootMenuTriggerState) return;
        if (triggerMode !== 'click') return;

        const openSiblingKey = getOpenSiblingSubmenuKey();
        if (!openSiblingKey) return;
        if (openSiblingKey === triggerKey) return;

        const openSiblingMode = getSubmenuTriggerMode(openSiblingKey);
        if (openSiblingMode !== 'hover') return;

        rootMenuTriggerState.closeSubmenu(openSiblingKey, submenuTriggerState.submenuLevel);
    };

    const handlePress = (event: PointerPressEvent) => {
        if (!isClickTriggerMode || !CLICK_MODE_TOGGLE_POINTER_TYPES.has(event.pointerType ?? '')) {
            onPress?.(event);
            return;
        }

        if (submenuTriggerState.isOpen) {
            submenuTriggerState.close();
            return;
        }

        onPress?.(event);
    };

    const handleHoverChange = (isHovered: boolean) => {
        // don't handle hover changes for click-mode triggers
        if (triggerMode === 'click') return;

        const openSiblingKey = getOpenSiblingSubmenuKey();
        const openSiblingMode = getSubmenuTriggerMode(openSiblingKey);

        // if a "click" mode sibling is open, don't handle hover changes
        if (openSiblingMode === 'click') return;

        onHoverChange?.(isHovered);
    };

    return {
        onPress: handlePress,
        onHoverStart: closeOpenHoverSiblingSubmenu,
        onHoverChange: handleHoverChange,
    };
};
